/**
 * Twitter Ads Adapter
 * 
 * Adapts event data into Twitter Pixel format.
 * 
 * Documentation:
 * - https://business.twitter.com/en/help/campaign-measurement-and-analytics/conversion-tracking-for-websites.html
 * 
 * @since 1.51.0
 */

(function (pmw, undefined) {

	class TwitterAdapter {
		
		/**
		 * Adapt event data to Twitter Pixel format
		 * 
		 * @param {object} eventData - Event-specific data (product, order, etc.)
		 * @param {string} eventName - Standardized event name
		 * @param {object} context - Event context (user, page, etc.)
		 * @returns {object|null} Adapted data for Twitter or null if pixel not loaded
		 */
		adapt(eventData, eventName, context) {
			
			// Check if Twitter pixel is loaded
			if (!pmwDataLayer?.pixels?.twitter?.loaded) {
				return null;
			}
			
			const eventMap = {
				'add_to_cart': () => this.adaptAddToCart(eventData),
				'view_item': () => this.adaptViewContent(eventData),
				'add_to_wishlist': () => this.adaptAddToWishlist(eventData),
				'begin_checkout': () => this.adaptInitiateCheckout(eventData),
				'add_payment_info': () => this.adaptAddPaymentInfo(eventData),
				'search': () => this.adaptSearch(eventData),
				'purchase': () => this.adaptPurchase(eventData),
			};
			
			if (!eventMap[eventName]) {
				return null;
			}
			
			return eventMap[eventName]();
		}
		
		/**
		 * Build product content data
		 */
		buildProductContent(product) {
			// Ensure product data exists in the data layer
			const productData = pmwDataLayer.products?.[product.id];
			if (!productData?.dyn_r_ids) {
				return null;
			}
			
			return {
				content_type: 'product',
				content_id: productData.dyn_r_ids[pmwDataLayer.pixels.twitter.dynamic_remarketing.id_type],
				content_name: product.name,
				content_price: product.price,
				num_items: product.quantity,
			};
		}
		
		/**
		 * Adapt add_to_cart event
		 */
		adaptAddToCart(eventData) {
			if (!eventData?.product) return null;
			
			const product = eventData.product;
			const eventId = pmwDataLayer?.pixels?.twitter?.event_ids?.add_to_cart;
			
			if (eventId) {
				const productContent = this.buildProductContent(product);
				if (!productContent) return null;
				
				return {
					event_type: 'event',
					event_id: eventId,
					event_data: {
						value: product.quantity * product.price,
						currency: pmwDataLayer?.shop?.currency,
						contents: [productContent],
					}
				};
			} else {
				// Legacy event
				return {
					event_type: 'track',
					event_name: 'AddToCart',
					event_data: null
				};
			}
		}
		
		/**
		 * Adapt view_item event
		 */
		adaptViewContent(eventData) {
			const eventId = pmwDataLayer?.pixels?.twitter?.event_ids?.view_content;
			let eventDataObj = {};
			
			if (eventData?.product) {
				const product = eventData.product;
				const productContent = this.buildProductContent(product);
				
				// Only add product data if buildProductContent succeeded
				if (productContent) {
					eventDataObj = {
						value: product.quantity * product.price,
						currency: pmwDataLayer?.shop?.currency,
						contents: [productContent],
					};
				}
			}
			
			if (eventId) {
				return {
					event_type: 'event',
					event_id: eventId,
					event_data: eventDataObj
				};
			} else {
				// Legacy event
				return {
					event_type: 'track',
					event_name: 'ViewContent',
					event_data: null
				};
			}
		}
		
		/**
		 * Adapt add_to_wishlist event
		 */
		adaptAddToWishlist(eventData) {
			if (!eventData?.product) return null;
			
			const product = eventData.product;
			const eventId = pmwDataLayer?.pixels?.twitter?.event_ids?.add_to_wishlist;
			let eventDataObj = {};
			
			if (product) {
				eventDataObj = {
					value: product.quantity * product.price,
					currency: pmwDataLayer?.shop?.currency,
					contents: [this.buildProductContent(product)],
				};
			}
			
			if (eventId) {
				return {
					event_type: 'event',
					event_id: eventId,
					event_data: eventDataObj
				};
			} else {
				// Legacy event
				return {
					event_type: 'track',
					event_name: 'AddToWishlist',
					event_data: null
				};
			}
		}
		
		/**
		 * Adapt begin_checkout event
		 */
		adaptInitiateCheckout(eventData) {
			const eventId = pmwDataLayer?.pixels?.twitter?.event_ids?.initiate_checkout;
			
			if (eventId) {
				return {
					event_type: 'event',
					event_id: eventId,
					event_data: {}
				};
			} else {
				// Legacy event
				return {
					event_type: 'track',
					event_name: 'InitiateCheckout',
					event_data: null
				};
			}
		}
		
		/**
		 * Adapt add_payment_info event
		 */
		adaptAddPaymentInfo(eventData) {
			const eventId = pmwDataLayer?.pixels?.twitter?.event_ids?.add_payment_info;
			
			if (eventId) {
				return {
					event_type: 'event',
					event_id: eventId,
					event_data: {}
				};
			} else {
				// Legacy event
				return {
					event_type: 'track',
					event_name: 'AddPaymentInfo',
					event_data: null
				};
			}
		}
		
		/**
		 * Adapt search event
		 */
		adaptSearch(eventData) {
			const eventId = pmwDataLayer?.pixels?.twitter?.event_ids?.search;
			
			if (eventId) {
				return {
					event_type: 'event',
					event_id: eventId,
					event_data: {
						search_string: pmw.getSearchTermFromUrl(),
					}
				};
			} else {
				// Legacy event
				return {
					event_type: 'track',
					event_name: 'Search',
					event_data: null
				};
			}
		}
		
		/**
		 * Adapt purchase event
		 */
		adaptPurchase(eventData) {
			const eventId = pmwDataLayer?.pixels?.twitter?.event_ids?.purchase;
			
			if (eventId) {
				return {
					event_type: 'event',
					event_id: eventId,
					event_data: {
						value: pmwDataLayer.order.value.marketing,
						currency: pmwDataLayer.order.currency,
						conversion_id: pmwDataLayer.order.id,
						contents: pmw.twitterGetOrderContentIdsV2(),
					}
				};
			} else {
				// Legacy event
				return {
					event_type: 'track',
					event_name: 'Purchase',
					event_data: {
						order_id: pmwDataLayer.order.id,
						value: pmwDataLayer.order.value.marketing,
						currency: pmwDataLayer.order.currency,
						num_items: pmwDataLayer.order.quantity,
						content_ids: pmw.twitterGetOrderContentIdsV1(),
					}
				};
			}
		}
	}
	
	// Register the adapter
	pmw.registerAdapter('twitter', new TwitterAdapter(), {
		supportsS2S: false,
		supportsEventDeduplication: false,
		isActive: () => pmwDataLayer?.pixels?.twitter?.pixel_id ? true : false
	});
	
}(window.pmw = window.pmw || {}));
